import React, { useMemo, useCallback } from "react";
import { Paper, Typography, Box, Button } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { makeStyles } from "@mui/styles";
import { RestoreSharp } from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	GridReadyEvent,
	ValueFormatterParams,
} from "ag-grid-community";
import { Alert } from "@mui/lab";
import { globalDefaultColDef, mesfTheme } from "@setup/ag-grid/mesf-theme";
import { useSearchSystemActivityStatistics } from "./hooks/useSearchSystemActivityStatistics";
import { SearchFilter } from "./filters/SearchFilter";

const useStyles = makeStyles(() => ({
	mainPaper: {
		padding: "24px",
	},
	gridContainer: {
		height: "calc(100vh - 350px)",
		width: "100%",
		minHeight: "500px",
		border: "1px solid #e0e0e0",
		borderRadius: "8px",
		"& .current-month-header": {
			background:
				"linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%) !important",
			color: "#1565c0 !important",
			fontWeight: "700 !important",
			textAlign: "center !important",
			borderRadius: "4px !important",
			boxShadow: "0 2px 4px rgba(33, 150, 243, 0.2) !important",
		},
		"& .total-header": {
			backgroundColor: "rgba(0, 0, 0, 0.06) !important",
			fontWeight: "700 !important",
		},
		"& .total-cell": {
			backgroundColor: "rgba(0, 0, 0, 0.035) !important",
		},
	},
}));

const cellStyles = {
	metric: {
		fontWeight: "500",
		color: "#333",
	},
	meltshopValue: {
		textAlign: "right" as const,
		fontFamily: "monospace",
		fontWeight: "bold",
		fontSize: "13px",
		color: "#1a73e8",
	},
	millValue: {
		textAlign: "right" as const,
		fontFamily: "monospace",
		fontWeight: "bold",
		fontSize: "13px",
		color: "#4caf50",
	},
	plantValue: {
		textAlign: "right" as const,
		fontFamily: "monospace",
		fontWeight: "bold",
		fontSize: "13px",
		color: "#7b1fa2",
	},
	servicesValue: {
		textAlign: "right" as const,
		fontFamily: "monospace",
		fontWeight: "bold",
		fontSize: "13px",
		color: "#ed6c02",
	},
};

export const SystemActivityStatistics: React.FC = () => {
	const classes = useStyles();
	const {
		statistics,
		loading,
		error,
		searchText,
		refetchStatistics,
		updateSearchText,
	} = useSearchSystemActivityStatistics();

	// Value formatter with memoization for performance
	const formatValue = useCallback((params: ValueFormatterParams): string => {
		const value = params.value;
		const metric = params.data?.Metric;
		const field = params.colDef?.field;

		// Global / non-area-attributed metrics show "-" in area columns when 0
		const isGlobalMetric =
			metric === "User Activity" ||
			metric === "Unique Users" ||
			metric === "New or updated documents";
		const isAreaColumn =
			field?.includes("Meltshop") ||
			field?.includes("Mill") ||
			field?.includes("Services");

		if (isGlobalMetric && isAreaColumn && value === 0) {
			return "-";
		}

		return value != null ? value.toLocaleString() : "0";
	}, []);

	const columnDefs = useMemo<ColDef[]>(() => {
		// Use the month names from the data (first row) or fallbacks
		const firstRow = statistics[0];
		const currentMonth =
			firstRow?.CurrentMonthName ||
			new Date().toLocaleDateString("en-US", {
				month: "long",
				year: "numeric",
			});
		const lastMonth =
			firstRow?.LastMonthName ||
			(() => {
				const date = new Date();
				date.setMonth(date.getMonth() - 1);
				return date.toLocaleDateString("en-US", {
					month: "long",
					year: "numeric",
				});
			})();

		return [
			{
				headerName: "Metric",
				field: "Metric",
				flex: 2,
				sortable: true,
				filter: "agTextColumnFilter",
				wrapText: true,
				autoHeight: true,
				cellStyle: () => cellStyles.metric,
			},
			{
				headerName: "Plant (All)",
				children: [
					{
						headerName: lastMonth,
						field: "PlantLastMonth",
						flex: 0.8,
						sortable: true,
						filter: "agNumberColumnFilter",
						cellStyle: () => cellStyles.plantValue,
						valueFormatter: formatValue,
					},
					{
						headerName: currentMonth,
						field: "PlantThisMonth",
						flex: 0.8,
						sortable: true,
						filter: "agNumberColumnFilter",
						cellStyle: () => cellStyles.plantValue,
						headerClass: "current-month-header",
						valueFormatter: formatValue,
					},
					{
						headerName: "Last 12 Months",
						field: "PlantLast12Months",
						flex: 0.8,
						sortable: true,
						filter: "agNumberColumnFilter",
						cellStyle: () => cellStyles.plantValue,
						valueFormatter: formatValue,
					},
					{
						headerName: "Total",
						field: "PlantTotal",
						flex: 0.8,
						sortable: true,
						filter: "agNumberColumnFilter",
						cellStyle: () => cellStyles.plantValue,
						headerClass: "total-header",
						cellClass: "total-cell",
						valueFormatter: formatValue,
					},
				],
			},
			{
				headerName: "Meltshop",
				children: [
					{
						headerName: lastMonth,
						field: "MeltshopLastMonth",
						flex: 0.8,
						sortable: true,
						filter: "agNumberColumnFilter",
						cellStyle: () => cellStyles.meltshopValue,
						valueFormatter: formatValue,
					},
					{
						headerName: currentMonth,
						field: "MeltshopThisMonth",
						flex: 0.8,
						sortable: true,
						filter: "agNumberColumnFilter",
						cellStyle: () => cellStyles.meltshopValue,
						headerClass: "current-month-header",
						valueFormatter: formatValue,
					},
					{
						headerName: "Last 12 Months",
						field: "MeltshopLast12Months",
						flex: 0.8,
						sortable: true,
						filter: "agNumberColumnFilter",
						cellStyle: () => cellStyles.meltshopValue,
						valueFormatter: formatValue,
					},
					{
						headerName: "Total",
						field: "MeltshopTotal",
						flex: 0.8,
						sortable: true,
						filter: "agNumberColumnFilter",
						cellStyle: () => cellStyles.meltshopValue,
						headerClass: "total-header",
						cellClass: "total-cell",
						valueFormatter: formatValue,
					},
				],
			},
			{
				headerName: "Rolling Mill",
				children: [
					{
						headerName: lastMonth,
						field: "MillLastMonth",
						flex: 0.8,
						sortable: true,
						filter: "agNumberColumnFilter",
						cellStyle: () => cellStyles.millValue,
						valueFormatter: formatValue,
					},
					{
						headerName: currentMonth,
						field: "MillThisMonth",
						flex: 0.8,
						sortable: true,
						filter: "agNumberColumnFilter",
						cellStyle: () => cellStyles.millValue,
						headerClass: "current-month-header",
						valueFormatter: formatValue,
					},
					{
						headerName: "Last 12 Months",
						field: "MillLast12Months",
						flex: 0.8,
						sortable: true,
						filter: "agNumberColumnFilter",
						cellStyle: () => cellStyles.millValue,
						valueFormatter: formatValue,
					},
					{
						headerName: "Total",
						field: "MillTotal",
						flex: 0.8,
						sortable: true,
						filter: "agNumberColumnFilter",
						cellStyle: () => cellStyles.millValue,
						headerClass: "total-header",
						cellClass: "total-cell",
						valueFormatter: formatValue,
					},
				],
			},
			{
				headerName: "Services",
				children: [
					{
						headerName: lastMonth,
						field: "ServicesLastMonth",
						flex: 0.8,
						sortable: true,
						filter: "agNumberColumnFilter",
						cellStyle: () => cellStyles.servicesValue,
						valueFormatter: formatValue,
					},
					{
						headerName: currentMonth,
						field: "ServicesThisMonth",
						flex: 0.8,
						sortable: true,
						filter: "agNumberColumnFilter",
						cellStyle: () => cellStyles.servicesValue,
						headerClass: "current-month-header",
						valueFormatter: formatValue,
					},
					{
						headerName: "Last 12 Months",
						field: "ServicesLast12Months",
						flex: 0.8,
						sortable: true,
						filter: "agNumberColumnFilter",
						cellStyle: () => cellStyles.servicesValue,
						valueFormatter: formatValue,
					},
					{
						headerName: "Total",
						field: "ServicesTotal",
						flex: 0.8,
						sortable: true,
						filter: "agNumberColumnFilter",
						cellStyle: () => cellStyles.servicesValue,
						headerClass: "total-header",
						cellClass: "total-cell",
						valueFormatter: formatValue,
					},
				],
			},
		];
	}, [statistics]);

	const handleGridReady = useCallback((params: GridReadyEvent) => {
		params.api.sizeColumnsToFit();
	}, []);

	if (error) {
		return (
			<Grid2 container spacing={3}>
				<Grid2 size={{ xs: 12 }}>
					<Paper style={{ padding: "24px", margin: "16px" }}>
						<Alert severity="error" style={{ marginBottom: "16px" }}>
							Failed to load production statistics: {error}
						</Alert>
						<Button
							variant="contained"
							color="primary"
							onClick={() => refetchStatistics()}
							startIcon={<RestoreSharp />}
						>
							Retry
						</Button>
					</Paper>
				</Grid2>
			</Grid2>
		);
	}

	return (
		<Grid2 container spacing={3} style={{ padding: "16px" }}>
			<Grid2 size={{ xs: 12 }}>
				<Typography
					variant="h5"
					style={{ margin: "0 0 20px", fontWeight: 600 }}
				>
					System Activity Statistics
				</Typography>
				<Paper className={classes.mainPaper}>
					<Box mb={3}>
						<SearchFilter
							searchText={searchText}
							onSearchChange={updateSearchText}
						/>
					</Box>

					<div className={classes.gridContainer}>
						<AgGridReact
							theme={mesfTheme}
							rowData={statistics}
							columnDefs={columnDefs}
							defaultColDef={globalDefaultColDef}
							onGridReady={handleGridReady}
							animateRows={true}
							getRowId={(params) => params.data.Metric}
							loading={loading}
							headerHeight={45}
							rowHeight={40}
							enableCellTextSelection={false}
							ensureDomOrder={true}
							suppressCellFocus={true}
							suppressRowHoverHighlight={false}
							suppressHorizontalScroll={false}
						/>
					</div>
				</Paper>
			</Grid2>
		</Grid2>
	);
};
